/**
 * ClaimSeatPage — /claim-your-seat?t=<token>
 *
 * Step 2 of a free sign-up: claim the free community seat (worker/src/
 * free-tier.ts; thread mct_bbd8f36a-310, brief mct_0fb77538-013). The token
 * is the person's link and carries no email, so name and email are never
 * asked again. The decided survey (mct_70dc6360-1e5, mcd_4f5d03bb-aa2):
 * country, business stage, where they want help first, one optional
 * free-text line (read by staff only), and an unticked marketing opt-in —
 * or, when they already said yes, CC's "you're subscribed" line instead.
 *
 * Every question, option and the opt-in sentence come from the API, which
 * reads Command Centre's feed. While CC's wording is not ready the API says
 * 'not_ready' and this page asks nothing: it has no words of its own to use.
 * The API 404s while the Free tier's intake switch is off, so this page shows
 * the "link not recognised" message until it is switched on. The "You're in"
 * and expired-link screens are Engine default text (not CC copy yet).
 *
 * Hardening (mct_b5801ee6-7de): the token is a capability, so it comes off
 * the address bar at once and the page sends no referrer; the page never
 * names anyone's tier; an expired link offers a fresh one by email; and a
 * person opening an inbox link with an unconfirmed opt-in can confirm it.
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import TakerShell from './components/TakerShell';

const API_BASE = (import.meta as unknown as { env: Record<string, string> }).env?.VITE_API_BASE_URL ?? '';
const ACCENT = '#F05A28';
const MUTED = '#6b7280';
const COMMUNITY_URL = 'https://community.thesuccessfulbookkeeper.com';

interface Choice { value: string; label: string }
interface Question {
  id: string;
  type: 'single_choice' | 'long_text';
  wording: string;
  help_text: string | null;
  required: boolean;
  options: Choice[];
  max_length: number | null;
}
interface ClaimCopy {
  version: number;
  page: { title: string; first_line: string } | null;
  questions: Question[];
  consent: { key: string; version: number; wording: string } | null;
}

/** CC's question ids, and the field each answer is posted as. */
const FIELD: Record<string, string> = { country: 'country', stage: 'business_stage', help_first: 'help_topic', next_months: 'next_months' };

type View =
  | { state: 'claim'; firstName: string | null; copy: ClaimCopy; subscribedLine: string | null }
  | { state: 'not_ready' }
  | { state: 'claimed'; firstName: string | null; confirmPending?: boolean }
  | { state: 'paid_member'; firstName: string | null }
  | { state: 'expired' };

type Phase =
  | { phase: 'loading' }
  | { phase: 'ready'; view: View }
  | { phase: 'done'; firstName: string | null; consent?: string }
  | { phase: 'error'; message: string };

const label: React.CSSProperties = { display: 'block', fontWeight: 600, margin: '18px 0 6px' };
const select: React.CSSProperties = { width: '100%', padding: '10px 12px', fontSize: 16, borderRadius: 8, border: '1px solid #CBD5E1', background: '#fff' };
const textarea: React.CSSProperties = { ...select, minHeight: 84, fontFamily: 'inherit', boxSizing: 'border-box' };
const button: React.CSSProperties = {
  marginTop: 24, padding: '12px 22px', borderRadius: 8, border: 'none', background: ACCENT, color: '#fff', fontSize: 16, fontWeight: 600, cursor: 'pointer',
};

function YoureIn({ firstName }: { firstName: string | null }) {
  return (
    <div data-testid="claim-seat-done">
      <h1 style={{ fontSize: 28, margin: '0 0 12px' }}>You’re in{firstName ? `, ${firstName}` : ''}.</h1>
      <p style={{ lineHeight: 1.6 }}>Your community login is on its way to your inbox. If you already have a community account, your free seat is on it: just log in.</p>
      <p style={{ lineHeight: 1.6 }}><strong>Where to start:</strong> the <a href={COMMUNITY_URL}>Start Here</a> area.</p>
      <p style={{ lineHeight: 1.6 }}><strong>One first action:</strong> introduce yourself in the community.</p>
    </div>
  );
}

/** The page must not pass its token on: no referrer from anything it loads or links to. */
function useNoReferrer() {
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'referrer';
    meta.content = 'no-referrer';
    document.head.appendChild(meta);
    return () => { meta.remove(); };
  }, []);
}

function Expired({ token }: { token: string }) {
  const [sent, setSent] = useState<'idle' | 'busy' | 'done' | 'error'>('idle');
  const ask = async () => {
    setSent('busy');
    try {
      const r = await fetch(`${API_BASE}/api/public/free-seat/fresh-link`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ t: token }),
      });
      setSent(r.ok ? 'done' : 'error');
    } catch {
      setSent('error');
    }
  };
  return (
    <div data-testid="claim-seat-expired">
      <h1 style={{ fontSize: 28, margin: '0 0 12px' }}>This link has expired</h1>
      {sent === 'done'
        ? <p style={{ lineHeight: 1.6 }}>If this link is yours, a fresh one is on its way to your inbox.</p>
        : (
          <>
            <p style={{ lineHeight: 1.6 }}>For your security, seat links stop working after a while. We can email a fresh link to the address we have on file.</p>
            <button type="button" style={button} disabled={sent === 'busy'} onClick={ask}>{sent === 'busy' ? 'Sending…' : 'Email me a fresh link'}</button>
            {sent === 'error' && <p role="alert" style={{ color: '#B91C1C', fontSize: 14 }}>That did not go through. Please try again.</p>}
          </>
        )}
    </div>
  );
}

function ConfirmOptIn({ token }: { token: string }) {
  const [state, setState] = useState<'idle' | 'busy' | 'done' | 'error'>('idle');
  const confirm = async () => {
    setState('busy');
    try {
      const r = await fetch(`${API_BASE}/api/public/free-seat/confirm`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ t: token }),
      });
      setState(r.ok ? 'done' : 'error');
    } catch {
      setState('error');
    }
  };
  if (state === 'done') return <p style={{ lineHeight: 1.6 }} data-testid="claim-seat-confirmed">Thanks, that's confirmed. Tips, events and offers will start arriving.</p>;
  return (
    <div style={{ marginTop: 24, padding: 16, border: '1px solid #E2E8F0', borderRadius: 8 }} data-testid="claim-seat-confirm">
      <p style={{ lineHeight: 1.6, margin: '0 0 8px' }}>You asked for tips, events and offers when you claimed your seat. Confirm it's you and we'll start sending them.</p>
      <button type="button" style={{ ...button, marginTop: 0 }} disabled={state === 'busy'} onClick={confirm}>{state === 'busy' ? 'Confirming…' : 'Confirm'}</button>
      {state === 'error' && <p role="alert" style={{ color: '#B91C1C', fontSize: 14 }}>That did not go through. Please try again.</p>}
    </div>
  );
}

export default function ClaimSeatPage() {
  const [params, setParams] = useSearchParams();
  // Read the token once, then take it off the address bar so no tag, history
  // entry or shared screenshot carries it (mct_b5801ee6-7de item 13).
  const [token] = useState(() => params.get('t') ?? '');
  useEffect(() => {
    if (params.has('t')) setParams({}, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useNoReferrer();
  const [state, setState] = useState<Phase>({ phase: 'loading' });
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    fetch(`${API_BASE}/api/public/free-seat?t=${encodeURIComponent(token)}`)
      .then(async (r) => {
        if (!live) return;
        if (!r.ok) {
          setState({ phase: 'error', message: 'We could not find your free seat from this link. Reply to your email and we will sort it out.' });
          return;
        }
        setState({ phase: 'ready', view: (await r.json()) as View });
      })
      .catch(() => live && setState({ phase: 'error', message: 'Something went wrong loading this page. Please try again in a moment.' }));
    return () => { live = false; };
  }, [token]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const qs = state.phase === 'ready' && state.view.state === 'claim' ? state.view.copy.questions : [];
    if (qs.some((q) => q.required && !answers[FIELD[q.id] ?? q.id])) {
      setError('Please answer the questions marked as needed.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/public/free-seat/claim`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ t: token, ...answers, marketing_consent: consent }),
      });
      if (r.status === 410) { setState({ phase: 'ready', view: { state: 'expired' } }); return; }
      if (r.status === 503) { setState({ phase: 'ready', view: { state: 'not_ready' } }); return; }
      if (!r.ok) { setError('That did not go through. Please check your answers and try again.'); return; }
      const data = (await r.json().catch(() => ({}))) as { state?: string; consent?: string };
      if (data.state === 'paid_member') {
        setState({ phase: 'ready', view: { state: 'paid_member', firstName } });
        return;
      }
      setState({ phase: 'done', firstName, consent: data.consent });
    } catch {
      setError('That did not go through. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const firstName = state.phase === 'ready' && 'firstName' in state.view ? state.view.firstName : null;

  return (
    <TakerShell>
      <main style={{ maxWidth: 560, margin: '0 auto', padding: '48px 16px 72px' }} data-testid="claim-seat-page">
        {state.phase === 'loading' && <p style={{ color: MUTED }}>Loading your free seat…</p>}

        {state.phase === 'error' && (
          <>
            <h1 style={{ fontSize: 28, margin: '0 0 12px' }}>Your free community seat</h1>
            <p role="alert">{state.message}</p>
          </>
        )}

        {state.phase === 'done' && (
          <>
            <YoureIn firstName={state.firstName} />
            {state.consent === 'unconfirmed' && (
              <p style={{ lineHeight: 1.6, color: MUTED, fontSize: 14 }}>You asked for tips, events and offers: we'll email you a link to confirm it's you first.</p>
            )}
          </>
        )}

        {state.phase === 'ready' && state.view.state === 'expired' && <Expired token={token} />}

        {state.phase === 'ready' && state.view.state === 'claimed' && (
          <>
            <YoureIn firstName={state.view.firstName} />
            {state.view.confirmPending && <ConfirmOptIn token={token} />}
          </>
        )}

        {state.phase === 'ready' && state.view.state === 'paid_member' && (
          <div data-testid="claim-seat-member">
            <h1 style={{ fontSize: 28, margin: '0 0 12px' }}>You’re already a member{state.view.firstName ? `, ${state.view.firstName}` : ''}.</h1>
            <p style={{ lineHeight: 1.6 }}>Your membership already includes the community. <a href={COMMUNITY_URL}>Log in here</a>.</p>
          </div>
        )}

        {state.phase === 'ready' && state.view.state === 'not_ready' && (
          <div data-testid="claim-seat-not-ready">
            <h1 style={{ fontSize: 28, margin: '0 0 12px' }}>Your free community seat</h1>
            <p style={{ lineHeight: 1.6 }}>This page is not quite ready. Please try your link again a little later.</p>
          </div>
        )}

        {state.phase === 'ready' && state.view.state === 'claim' && (() => {
          const { copy, subscribedLine } = state.view;
          const set = (field: string, v: string) => setAnswers({ ...answers, [field]: v });
          return (
            <form onSubmit={submit} data-testid="claim-seat-form">
              {copy.page && <h1 style={{ fontSize: 28, margin: '0 0 8px' }}>{copy.page.title}</h1>}
              {copy.page && <p style={{ lineHeight: 1.6, color: '#334155', margin: 0 }}>{copy.page.first_line}</p>}

              {copy.questions.map((q) => {
                const field = FIELD[q.id] ?? q.id;
                const id = `cs-${q.id}`;
                return (
                  <div key={q.id}>
                    <label style={label} htmlFor={id}>{q.wording}</label>
                    {q.help_text && <p style={{ color: MUTED, fontSize: 13, margin: '-2px 0 8px' }}>{q.help_text}</p>}
                    {q.type === 'single_choice' ? (
                      <select id={id} style={select} value={answers[field] ?? ''} required={q.required} onChange={(e) => set(field, e.target.value)}>
                        <option value="">Choose one…</option>
                        {q.options.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                    ) : (
                      <textarea id={id} style={textarea} value={answers[field] ?? ''} maxLength={q.max_length ?? 300} onChange={(e) => set(field, e.target.value)} />
                    )}
                  </div>
                );
              })}

              {subscribedLine ? (
                <p style={{ marginTop: 20, lineHeight: 1.5, fontSize: 14 }} data-testid="claim-seat-subscribed">{subscribedLine}</p>
              ) : copy.consent && (
                <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 20, lineHeight: 1.5, fontSize: 14 }}>
                  <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} style={{ marginTop: 4 }} data-testid="claim-seat-consent" />
                  <span>{copy.consent.wording}</span>
                </label>
              )}

              {error && <p role="alert" style={{ color: '#B91C1C', fontSize: 14 }}>{error}</p>}
              <button type="submit" style={button} disabled={busy}>{busy ? 'Claiming your seat…' : 'Claim my seat'}</button>
            </form>
          );
        })()}
      </main>
    </TakerShell>
  );
}
